import { GRANDMOTHER_NODE_ID } from "../domain/initial-patch";
import { cableEndpoints } from "../domain/patch";
import type {
  CableEndpoint,
  ExternalEndpointNode,
  PatchDocument,
  UtilityNode,
} from "../domain/patch";
import type {
  InstrumentDefinition,
  PortDefinition,
  SignalConnectionDefinition,
} from "../domain/instrument";
import { instrumentBundleForDocument } from "../instruments/catalog";
import { cachedCompiledInstrumentRuntime } from "./compiled-instrument";

export const EXTERNAL_PORT_PREFIX = "external:";
export const UTILITY_PORT_PREFIX = "utility:";
export const EXTERNAL_CARD_WIDTH = 210;
export const EXTERNAL_CARD_HEIGHT = 96;
export const UTILITY_CARD_WIDTH = 220;
export const UTILITY_GROUP_HEIGHT = 68;
export const EXTERNAL_WORKSPACE_GAP = 26;

export function externalEndpoints(document: PatchDocument): ExternalEndpointNode[] {
  return document.workspace.nodes.filter(
    (node): node is ExternalEndpointNode => node.kind === "external-endpoint",
  );
}

export function utilityNodes(document: PatchDocument): UtilityNode[] {
  return document.workspace.nodes.filter((node): node is UtilityNode => node.kind === "utility");
}

export function externalPortId(nodeId: string) {
  return `${EXTERNAL_PORT_PREFIX}${nodeId}`;
}

export function utilityPortId(nodeId: string, portId: string) {
  return `${UTILITY_PORT_PREFIX}${nodeId}:${portId}`;
}

export function endpointForPortId(portId: string): CableEndpoint {
  let nodeId = GRANDMOTHER_NODE_ID;
  if (portId.startsWith(EXTERNAL_PORT_PREFIX)) {
    nodeId = portId.slice(EXTERNAL_PORT_PREFIX.length);
  } else if (portId.startsWith(UTILITY_PORT_PREFIX)) {
    nodeId = portId.slice(UTILITY_PORT_PREFIX.length).split(":")[0] ?? GRANDMOTHER_NODE_ID;
  }
  return { kind: "port", nodeId, portId };
}

export function externalPortDefinition(node: ExternalEndpointNode): PortDefinition {
  return {
    id: externalPortId(node.id),
    sectionId: "external-equipment",
    label: node.portName
      ? `${node.deviceName ?? node.displayName} — ${node.portName}`
      : node.displayName,
    shortLabel: node.portName ?? node.displayName,
    position: {
      x: node.position.x + EXTERNAL_CARD_WIDTH / 2,
      y: node.position.y,
    },
    direction: node.direction,
    signalType: node.signalType,
    location: "rear",
    description: node.description?.trim() || undefined,
    electrical: node.voltageNotes?.trim() ? { range: node.voltageNotes.trim() } : undefined,
    size: 18,
  };
}

export function utilityPortDefinitions(node: UtilityNode): PortDefinition[] {
  return node.ports.map((port) => ({
    id: utilityPortId(node.id, port.id),
    sectionId: "utility-equipment",
    label: `${node.displayName} — ${port.label}`,
    shortLabel: port.label,
    position: {
      x: node.position.x + port.position.x * node.scale,
      y: node.position.y + port.position.y * node.scale,
    },
    direction: port.direction ?? "bidirectional",
    signalType: port.signalType,
    location: "rear",
    description:
      node.userNotes?.trim() ||
      (node.utilityType.includes("attenuator")
        ? (port.direction === "input" ? "Input to" : "Output from") + " adjustable attenuator."
        : node.utilityType === "splitter-cable"
          ? "Electrically common branch of a passive splitter cable."
          : node.utilityType === "stackable-junction"
            ? "Electrically common end of a movable passive cable junction."
            : "Electrically common passive mult socket."),
    size: 18,
  }));
}

function utilityConnections(node: UtilityNode): SignalConnectionDefinition[] {
  if (node.utilityType === "inline-attenuator" || node.utilityType === "standalone-attenuator") {
    const input = node.ports.find((port) => port.direction === "input");
    const output = node.ports.find((port) => port.direction === "output");
    if (!input || !output) return [];
    return [
      {
        id: "utility-route:" + node.id + ":attenuator",
        fromId: utilityPortId(node.id, input.id),
        toId: utilityPortId(node.id, output.id),
        signalType: "mixed",
        label: node.displayName + " signal path",
        description: "Directional signal path through an adjustable attenuator.",
        bidirectional: false,
      },
    ];
  }

  const groups = new Map<string, UtilityNode["ports"]>();
  for (const port of node.ports) {
    groups.set(port.groupId, [...(groups.get(port.groupId) ?? []), port]);
  }
  return [...groups.entries()].flatMap(([groupId, ports]) => {
    const first = ports[0];
    if (!first) return [];
    return ports.slice(1).map((port) => ({
      id: `utility-route:${node.id}:${groupId}:${port.id}`,
      fromId: utilityPortId(node.id, first.id),
      toId: utilityPortId(node.id, port.id),
      signalType: "mixed" as const,
      label: `${node.displayName} ${groupId}`,
      description:
        node.utilityType === "splitter-cable"
          ? "Passive electrical branch through a splitter cable."
          : node.utilityType === "stackable-junction"
            ? "Passive electrical route through a movable cable junction."
            : "Passive electrical connection between mult sockets.",
      bidirectional: true,
    }));
  });
}

export function compiledInstrumentDefinitionForDocument(document: PatchDocument) {
  const bundle = instrumentBundleForDocument(document);
  return cachedCompiledInstrumentRuntime(bundle.document).definition;
}

export function workspaceDefinition(
  document: PatchDocument,
  instrument: InstrumentDefinition = compiledInstrumentDefinitionForDocument(document),
): InstrumentDefinition {
  const endpoints = externalEndpoints(document);
  const utilities = utilityNodes(document);
  if (!endpoints.length && !utilities.length) return instrument;
  return {
    ...instrument,
    ports: [
      ...instrument.ports,
      ...endpoints.map(externalPortDefinition),
      ...utilities.flatMap(utilityPortDefinitions),
    ],
    signalConnections: [...instrument.signalConnections, ...utilities.flatMap(utilityConnections)],
  };
}

export function defaultExternalPosition(index: number, document: PatchDocument) {
  const instrument = compiledInstrumentDefinitionForDocument(document);
  const columns = 6;
  return {
    x: 240 + (index % columns) * (EXTERNAL_CARD_WIDTH + 18),
    y:
      instrument.panel.height +
      EXTERNAL_WORKSPACE_GAP +
      Math.floor(index / columns) * (EXTERNAL_CARD_HEIGHT + 18),
  };
}

export function utilityCardWidth(node: UtilityNode) {
  return node.visualStyle === "inline" ? 180 : UTILITY_CARD_WIDTH;
}

export function utilityCardHeight(node: UtilityNode) {
  if (node.utilityType === "inline-attenuator") return 112;
  if (node.utilityType === "standalone-attenuator") return 132;
  if (node.utilityType === "splitter-cable") return 148;
  if (node.utilityType === "stackable-junction") return 150;
  return Math.max(
    96,
    new Set(node.ports.map((port) => port.groupId)).size * UTILITY_GROUP_HEIGHT + 34,
  );
}

export function defaultUtilityPosition(index: number, document: PatchDocument) {
  const instrument = compiledInstrumentDefinitionForDocument(document);
  const endpoints = externalEndpoints(document);
  const endpointBottom = endpoints.length
    ? Math.max(...endpoints.map((node) => node.position.y + EXTERNAL_CARD_HEIGHT))
    : instrument.panel.height;
  return {
    x: 240 + (index % 5) * (UTILITY_CARD_WIDTH + 22),
    y: endpointBottom + EXTERNAL_WORKSPACE_GAP + Math.floor(index / 5) * 130,
  };
}

export function workspaceWorldHeight(
  document: PatchDocument,
  instrument: InstrumentDefinition = compiledInstrumentDefinitionForDocument(document),
) {
  const endpoints = externalEndpoints(document);
  const utilities = utilityNodes(document);
  return Math.max(
    instrument.panel.height,
    ...endpoints.map((node) => node.position.y + EXTERNAL_CARD_HEIGHT + EXTERNAL_WORKSPACE_GAP),
    ...utilities.map(
      (node) => node.position.y + utilityCardHeight(node) * node.scale + EXTERNAL_WORKSPACE_GAP,
    ),
    ...document.workspace.cableAssemblies.flatMap((cable) =>
      cableEndpoints(cable)
        .filter((endpoint) => endpoint.kind === "loose")
        .map((endpoint) =>
          endpoint.kind === "loose" ? endpoint.y + EXTERNAL_WORKSPACE_GAP : instrument.panel.height,
        ),
    ),
    ...document.workspace.cableAssemblies
      .filter((cable) => cable.junction)
      .map((cable) => (cable.junction?.y ?? 0) + EXTERNAL_WORKSPACE_GAP),
  );
}
